import { ItemType } from "@/hooks/form/configuration/useItemConfigurations";
import { Item, ItemGroup } from "@/hooks/form/source-data/useItemsVirtualized";
import { Box, Button, Divider, List, ListItem, ListItemIcon, ListItemText, Tooltip, TooltipProps, alpha, styled } from "@mui/material";
import { FC, Fragment, memo, useContext, useMemo } from "react";
import SourceDataFormMessages from "../messages/SourceDataFormMessages";
import SourceDataFormReviewAndVerificationButton from "../buttons/SourceDataFormReviewAndVerificationButton";
import SourceDataFormFindingButton from "../buttons/SourceDataFormFindingButton";
import SourceDataFormNoteButton from "../buttons/SourceDataFormNoteButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { SourceDataItemMapping } from "../values/Index";
import { SourceDataFieldType, SourceDataFormStatus, SourceDataItemGroupType, SourceDataPriority } from "@/api/dtos";
import useItem from "@/hooks/form/source-data/useItem";
import { AppSettings } from "@/AppSettings";
import useItemGroup from "@/hooks/form/source-data/useItemGroup";
import { Virtuoso } from "react-virtuoso";
import useForm from "@/hooks/form/source-data/useForm";
import useEvent from "@/hooks/form/source-data/useEvent";
import SourceDataFormAuditButton from "../buttons/SourceDataFormAuditButton";
import SourceDataFormButtons from "../buttons/SourceDataFormButtons";
import SourceDataFormSettingsContext from "@/contexts/form/SourceDataFormSettingsContext";
import SourceDataItemGroupGridContext from "@/contexts/form/SourceDataItemGroupGridContext";
import { CounterButton } from "../buttons";


const StyledListItem = styled(ListItem)(({theme, color}) => {

    return {
        padding: theme.spacing(1, 2),
        minHeight: theme.spacing(8),
        //@ts-ignore
        background: color ? alpha((theme.palette[color]).main, 0.10) : undefined
    }
})

const StyledListItemText = styled(ListItemText)(({ theme }) => ({
    margin: theme.spacing(0.5, 0)
}));

const StyledListItemIcon = styled(ListItemIcon)(({ theme }) => ({
    minWidth: theme.spacing(6)
}));

const IndentBox = styled(Box)(({ theme }) => ({
    marginLeft: theme.spacing(8),
    borderLeft: `1px solid ${alpha(theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black, 0.12)}`

}));

const IndentDivider = styled(Divider)(({ theme }) => ({
    borderColor: alpha(theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black, 0.12)
}));

const RepeatBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    whiteSpace: 'pre',
    minHeight: theme.spacing(5.25),
    minWidth: theme.spacing(10.5),
    alignItems: 'center',
    borderStyle: 'solid',
    borderWidth: 1,
    borderRadius: theme.shape.borderRadius,
    borderColor: theme.palette.mode === 'dark' ?
        alpha(theme.palette.common.white, 0.12):
        alpha(theme.palette.common.black, 0.12),
    background: theme.palette.mode === 'dark' ?
        theme.palette.grey[800] :
        theme.palette.grey[100],

    '& > *': {
        minWidth: theme.spacing(5.25),
        textAlign: 'center'
    }
}))

const RepeatIconBox = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1, 1),
    alignSelf: 'stretch',
    textAlign: 'center',
    minWidth: theme.spacing(5.25),
    borderStyle: 'solid',
    borderWidth: 0,
    borderRightWidth: 1,
    borderRadius: 0,
    borderColor: theme.palette.mode === 'dark' ?
        alpha(theme.palette.common.white, 0.12):
        alpha(theme.palette.common.black, 0.12),
    background: theme.palette.mode === 'dark' ?
        theme.palette.grey[700] :
        theme.palette.grey[200]
}))

export const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    marginTop: '12px !important'
}));

export const SourceDataFormItemGroupDetails: FC<{ 
    itemGroupData?: ReturnType<typeof useItemGroup>; 
    divider?: boolean; 
    form: ReturnType<typeof useForm>;
    event: ReturnType<typeof useEvent>;
    hidePriority: boolean;
}> = ({
    itemGroupData,
    divider
}) => {
    const { setItemGroupName } = useContext(SourceDataItemGroupGridContext);

    if (!itemGroupData) {
        return null;
    }

    return (
        <>
            <StyledListItem>
                <StyledListItemIcon>
                    <FontAwesomeIcon fixedWidth size="lg" icon={itemGroupData.icon} />
                </StyledListItemIcon>
                <StyledListItemText 
                    primary={itemGroupData.displayName} 
                />
                {
                    itemGroupData.itemGroupMetadata.type === SourceDataItemGroupType.Grid && (
                        <Tooltip title="View as Grid">
                            <CounterButton
                                onClick={() => setItemGroupName(itemGroupData.itemGroupName)}
                                variant="outlined"
                                sx={{ mr: 2}}
                            >
                                <FontAwesomeIcon icon={AppSettings.icons.itemGrid} size="2x" />
                            </CounterButton>
                        </Tooltip>
                    )
                }
                <Tooltip title={<>Row Number &mdash; {itemGroupData.itemGroupRepeat}</>}>
                    <RepeatBox>
                        <RepeatIconBox>
                            <FontAwesomeIcon icon={AppSettings.icons.itemGroupRepeat} size="lg" />
                        </RepeatIconBox>
                        <Box>
                            {itemGroupData.itemGroupRepeat}
                        </Box>
                    </RepeatBox>
                </Tooltip>
            </StyledListItem>
            { divider && <Divider /> }
            <SourceDataFormMessages
                alertSuccess={itemGroupData.configuration?.alertSuccess}
                alertInformation={itemGroupData.configuration?.alertInformation}
                alertWarning={itemGroupData.configuration?.alertWarning}
                alertError={itemGroupData.configuration?.alertError}
            />
        </>
    );
};

export const SourceDataFormItemDetails: FC<{ 
    itemData?: ReturnType<typeof useItem>; 
    itemGroupData?: ReturnType<typeof useItemGroup>; 
    divider?: boolean; 
    form: ReturnType<typeof useForm>;
    event: ReturnType<typeof useEvent>;
    hidePriority: boolean;
    allowReview?: boolean;
    allowVerification?: boolean;
}> = ({
    itemData,
    itemGroupData,
    divider,
    form,
    event,
    hidePriority,
    allowReview,
    allowVerification
}) => {

    const priority = useMemo(() => {
        let priority = event?.priority ?? SourceDataPriority.Normal;

        if (form?.priority && form.priority !== SourceDataPriority.Inherit) {
            priority = form.priority;
        }

        if (form?.formVersion?.priority && form.formVersion.priority !== SourceDataPriority.Inherit) {
            priority = form.formVersion.priority;
        }

        if (itemGroupData?.priority && itemGroupData.priority !== SourceDataPriority.Inherit) {
            priority = itemGroupData.priority;
        }

        if (itemData?.priority && itemData.priority !== SourceDataPriority.Inherit) {
            priority = itemData.priority;
        }

        if (itemData?.itemValueConfiguration?.priority && itemData?.itemValueConfiguration?.priority !== SourceDataPriority.Inherit) {
            priority = itemData.itemValueConfiguration.priority;
        }


        switch (priority) {
            case SourceDataPriority.Low:
                return {
                    icon: (
                        <StyledTooltip
                            title="Priority: Low"
                            followCursor
                        >
                            <StyledListItemIcon 
                                sx={{ color: 'success.main' }} 
                            >
                                <FontAwesomeIcon fixedWidth size="lg" icon={AppSettings.icons.priorityLow} />
                            </StyledListItemIcon>
                        </StyledTooltip>
                    ),
                    color: 'success',
                    priority: SourceDataPriority.Low
                }
            case SourceDataPriority.High:
                return {
                    icon: (
                        <StyledTooltip
                            title="Priority: High"
                            followCursor
                        >
                            <StyledListItemIcon 
                                sx={{ color: 'warning.main' }} 
                            >
                                <FontAwesomeIcon fixedWidth size="lg" icon={AppSettings.icons.priorityHigh} />
                            </StyledListItemIcon>
                        </StyledTooltip>
                    ),
                    color: 'warning',
                    priority: SourceDataPriority.High
                }
            case SourceDataPriority.Critical:
                return {
                    icon: (
                        <StyledTooltip
                            title="Priority: Critical"
                            followCursor
                        >
                            <StyledListItemIcon 
                                sx={{ color: 'error.main' }} 
                            >
                                <FontAwesomeIcon fixedWidth size="lg" icon={AppSettings.icons.priorityCritical} />
                            </StyledListItemIcon>
                        </StyledTooltip>
                    ),
                    color: 'error',
                    priority: SourceDataPriority.Critical
                }
            case SourceDataPriority.Normal:
            default:
                return {
                    icon: (
                        <StyledTooltip
                            title="Priority: Normal"
                            followCursor
                        >
                            <StyledListItemIcon>
                                <FontAwesomeIcon fixedWidth size="lg" icon={AppSettings.icons.priorityNormal} />
                            </StyledListItemIcon>
                        </StyledTooltip>
                    ),
                    color: undefined,
                    priority: SourceDataPriority.Normal
                }
        }
    }, [form, event, itemData, itemGroupData])

    if (!itemData || itemData.itemMetadata.fieldType === SourceDataFieldType.Hidden) {
        return null;
    }

    const ValueComponent = SourceDataItemMapping[itemData.itemMetadata.fieldType ?? SourceDataFieldType.Text];

    return (
        <>
            {
                itemData.itemMetadata.header && (
                    <>
                        <StyledListItem 
                            //color={priority.color}
                        >
                            {!hidePriority && <StyledListItemIcon />}
                            <StyledListItemText 
                                sx={{minWidth: 'unset', mr: 2, fontWeight: 'bold' }}
                                primary={itemData.itemMetadata.header} 
                            />
                        </StyledListItem>
                        { divider && <Divider /> }
                    </>
                )
            }
            <StyledListItem>
                {!hidePriority && priority.icon}
                <StyledListItemText 
                    sx={{minWidth: 'unset', mr: 2}}
                    primary={itemData.displayName} 
                />
                <ValueComponent itemData={itemData} />
                <SourceDataFormButtons
                    form={form}
                    itemData={itemData} 
                    priority={priority.priority} 
                    allowReview={allowReview} 
                    allowVerification={allowVerification}
                    itemGroupAllowReview={itemGroupData ? itemGroupData?.configuration?.allowReview : true}
                    itemGroupAllowVerification={itemGroupData ? itemGroupData?.configuration?.allowVerification : true}
                />
            </StyledListItem>
            { divider && <Divider /> }
            <SourceDataFormMessages
                alertSuccess={itemData.configuration?.alertSuccess}
                alertInformation={itemData.configuration?.alertInformation}
                alertWarning={itemData.configuration?.alertWarning}
                alertError={itemData.configuration?.alertError}
            />
            <SourceDataFormMessages
                alertSuccess={itemData.itemValueConfiguration?.configuration?.alertSuccess}
                alertInformation={itemData.itemValueConfiguration?.configuration?.alertInformation}
                alertWarning={itemData.itemValueConfiguration?.configuration?.alertWarning}
                alertError={itemData.itemValueConfiguration?.configuration?.alertError}
            />
        </>
    );
};

interface ISourceDataItemListProps {
    itemData: Array<Item | ItemGroup>;
    form: ReturnType<typeof useForm>;
    event: ReturnType<typeof useEvent>;
    allowReview?: boolean;
    allowVerification?: boolean;
}





interface IVirtuosoContext {
    length: number;
    form: ReturnType<typeof useForm>;
    event: ReturnType<typeof useEvent>;
    hidePriority: boolean;
    allowReview?: boolean;
    allowVerification?: boolean;
}

interface IVirtuosoInnerItem {
    index: number;
    item: Item | ItemGroup;
    context: IVirtuosoContext;
}


// Item contents are cached properly with React.memo
const InnerItem =({ 
    index,
    item,
    context
}: IVirtuosoInnerItem) => {
    const notLastItem = (index + 1) < context.length;

    if (item.type === ItemType.ItemGroup) {
        return (
            <Fragment
                key={`${item.itemGroup?.itemGroupName}-${item.primaryRepeat}`}
            >
                <SourceDataFormItemGroupDetails
                    itemGroupData={item.itemGroup}
                    divider={notLastItem}
                    form={context.form}
                    event={context.event}
                    hidePriority={context.hidePriority}
                />
            </Fragment>
        );
    }

    if (item.itemGroup) {
        return (
            <>
                <IndentBox>
                    <SourceDataFormItemDetails
                        key={`${item.primaryRepeat}-${item.item.itemName}`}
                        itemData={item.item}
                        itemGroupData={item.itemGroup}
                        divider={notLastItem && !item.lastInGroup}
                        form={context.form}
                        event={context.event}
                        hidePriority={context.hidePriority}
                        allowReview={context.allowReview}
                        allowVerification={context.allowVerification}
                    />
                </IndentBox>
                {notLastItem && item.lastInGroup && <IndentDivider />}
            </>
        )
    }

    return (
        <SourceDataFormItemDetails
            key={item.item.itemName}
            itemData={item.item}
            divider={notLastItem}
            form={context.form}
            event={context.event}
            hidePriority={context.hidePriority}
            allowReview={context.allowReview}
            allowVerification={context.allowVerification}
        />
    )
};

const MemoizedInnerItem = memo(InnerItem) as typeof InnerItem;

// The callback is executed often - don't inline complex components in here.
const virtuosoItemContent = (index: number, item: Item | ItemGroup, context: IVirtuosoContext) => {
    return <MemoizedInnerItem index={index} item={item} context={context} />
}


const SourceDataItemList: FC<ISourceDataItemListProps> = ({
    itemData,
    form,
    event,
    allowReview,
    allowVerification
}) => {
    
    const {
        settings: {
            hidePriorityIcon
        }
    } = useContext(SourceDataFormSettingsContext);

    const virtuosoContext = useMemo(() => {
        return {
            length: itemData.length,
            form,
            event,
            hidePriority: hidePriorityIcon,
            allowReview,
            allowVerification
        }
    }, [
        itemData,
        form,
        event,
        hidePriorityIcon,
        allowReview,
        allowVerification
    ]);

    return (
        <List disablePadding>
            {
                itemData.length > 20 && (
                    <Virtuoso 
                        useWindowScroll
                        data={itemData}
                        context={virtuosoContext}
                        itemContent={virtuosoItemContent}
                    />  
                )
            }
            {
                itemData.length <= 20 && (
                    itemData.map((x, i) => (
                        <MemoizedInnerItem key={i} index={i} item={x} context={virtuosoContext} />
                    ))
                )
            }  
        </List>
    )
}

export default SourceDataItemList;